import React, { useEffect, useState } from 'react';
import { http, authCfg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Users, Plus, Loader2, ClipboardList, Banknote, ChevronDown, Trash2, Copy, Mail, X } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const EMPTY_FORM = { name: '', mobile: '', email: '', password: '', affiliateCode: '', commissionPercent: 5 };

export default function CrewAdminPanel() {
  const { toast } = useToast();
  const [list, setList] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [task, setTask] = useState({ title: '', details: '', dueDate: '' });
  const [busy, setBusy] = useState(false);
  const [welcome, setWelcome] = useState(null); // {id, name, welcomeText, email}
  const [welcomeEmail, setWelcomeEmail] = useState('');
  const [sendingWelcome, setSendingWelcome] = useState(false);

  const load = () => http.get('/admin/crew', authCfg()).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id); setDetail(null);
    const { data } = await http.get(`/admin/crew/${id}/detail`, authCfg());
    setDetail(data);
  };

  const create = async () => {
    if (!form.name || !form.mobile || (form.password || '').length < 6) {
      toast({ title: 'Name, mobile number and a 6+ character password are required', variant: 'destructive' }); return;
    }
    setBusy(true);
    try {
      const { data } = await http.post('/admin/crew', form, authCfg());
      toast({ title: `${form.name} added — they sign in at /crew with their mobile` });
      setWelcome(data); setWelcomeEmail(form.email || '');
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not add', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const copyWelcome = async () => {
    try { await navigator.clipboard.writeText(welcome.welcomeText); toast({ title: 'Welcome message copied — paste it into a text or WhatsApp' }); }
    catch { toast({ title: 'Could not copy — select the text and copy manually', variant: 'destructive' }); }
  };

  const sendWelcomeEmail = async (id, email) => {
    if (!email) { toast({ title: 'Enter an email address first', variant: 'destructive' }); return; }
    setSendingWelcome(true);
    try {
      const { data } = await http.post(`/admin/crew/${id}/welcome-email`, { email }, authCfg());
      toast({ title: data.status === 'sent' ? `Welcome email sent to ${data.to}` : `Welcome email ${data.status} (SMTP not configured)` });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not send', variant: 'destructive' }); }
    finally { setSendingWelcome(false); }
  };

  const resendWelcome = async (c) => {
    const em = c.email || window.prompt('Email address to send the welcome to:');
    if (!em) return;
    await sendWelcomeEmail(c.id, em);
    load();
  };

  const addTask = async (id) => {
    if (!task.title.trim()) { toast({ title: 'Give the task a title', variant: 'destructive' }); return; }
    try {
      await http.post(`/admin/crew/${id}/tasks`, task, authCfg());
      setTask({ title: '', details: '', dueDate: '' });
      toast({ title: 'Task allocated' });
      openDetail(id); setOpenId(id); load();
      const { data } = await http.get(`/admin/crew/${id}/detail`, authCfg()); setDetail(data);
    } catch (e) { toast({ title: 'Could not add task', variant: 'destructive' }); }
  };

  const delTask = async (id, taskId) => {
    await http.delete(`/admin/crew/tasks/${taskId}`, authCfg());
    const { data } = await http.get(`/admin/crew/${id}/detail`, authCfg()); setDetail(data); load();
  };

  const pay = async (c) => {
    if (!window.confirm(`Mark ${money(c.totalOwed)} as paid to ${c.name}? This locks their unpaid work entries.`)) return;
    try {
      const { data } = await http.post(`/admin/crew/${c.id}/pay`, { note: '' }, authCfg());
      toast({ title: `Paid ${money(data.paid)} (${money(data.workAmount)} work + ${money(data.commissionAmount)} commission)` });
      load(); if (openId === c.id) { const { data: d } = await http.get(`/admin/crew/${c.id}/detail`, authCfg()); setDetail(d); }
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not record payment', variant: 'destructive' }); }
  };

  const toggleActive = async (c) => {
    await http.put(`/admin/crew/${c.id}`, { name: c.name, affiliateCode: c.affiliateCode, commissionPercent: c.commissionPercent, active: !c.active, username: c.username, mobile: c.mobile, email: c.email }, authCfg());
    load();
  };

  if (!list) return <div className="py-16 text-center"><Loader2 className="animate-spin inline text-alp-gold" size={20} /></div>;

  return (
    <div data-testid="crew-admin-panel">
      <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2 mb-1"><Users size={20} className="text-alp-gold" /> Crew</h2>
      <p className="text-alp-gray text-sm mb-6">Contractors sign in at <span className="text-alp-gold">/crew</span> with their <span className="text-alp-gold">mobile number</span>. Their referral code gives customers <span className="text-alp-gold">10% off</span> and earns them commission. They see only their tasks, work log and own commission — never your sales figures or client details.</p>

      <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5 mb-8">
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Add a crew member</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="crew-new-name" className="bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream" />
          <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile (their login)" data-testid="crew-new-mobile" className="bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (opt)" data-testid="crew-new-email" className="bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream" />
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (6+)" data-testid="crew-new-password" className="bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream" />
          <input value={form.affiliateCode} onChange={(e) => setForm({ ...form, affiliateCode: e.target.value })} placeholder="Referral code (opt)" data-testid="crew-new-code" className="bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream" />
          <button onClick={create} disabled={busy} data-testid="crew-new-save" className="bg-alp-gold text-alp-black rounded px-4 py-2.5 text-xs tracking-[0.12em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50 inline-flex items-center justify-center gap-1"><Plus size={14} /> Add</button>
        </div>
      </div>

      {welcome && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" data-testid="crew-welcome-modal">
          <div className="w-full max-w-lg border border-alp-gold/40 rounded-xl bg-alp-dark p-6 relative">
            <button onClick={() => setWelcome(null)} data-testid="crew-welcome-close" className="absolute top-3 right-3 text-alp-gray hover:text-alp-gold"><X size={16} /></button>
            <p className="font-serif text-xl text-alp-gold mb-1">Welcome {welcome.name} to the crew 🎉</p>
            <p className="text-alp-gray text-xs mb-4">Send their welcome by email, or copy the message and text/WhatsApp it to them.</p>
            <textarea readOnly value={welcome.welcomeText} data-testid="crew-welcome-text" rows={10}
              className="w-full bg-alp-black border border-alp-line rounded px-3 py-2.5 text-xs text-alp-cream/90 mb-4 leading-relaxed" />
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={copyWelcome} data-testid="crew-welcome-copy"
                className="inline-flex items-center gap-1.5 bg-alp-gold text-alp-black px-4 py-2.5 rounded text-xs tracking-[0.12em] uppercase font-semibold hover:bg-alp-goldlight"><Copy size={14} /> Copy message</button>
              <input value={welcomeEmail} onChange={(e) => setWelcomeEmail(e.target.value)} placeholder="their@email.com" data-testid="crew-welcome-email-input"
                className="flex-1 min-w-[160px] bg-alp-black border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream" />
              <button onClick={() => sendWelcomeEmail(welcome.id, welcomeEmail.trim())} disabled={sendingWelcome} data-testid="crew-welcome-send-email"
                className="inline-flex items-center gap-1.5 border border-alp-gold/50 text-alp-gold px-4 py-2.5 rounded text-xs tracking-[0.12em] uppercase hover:bg-alp-gold/10 disabled:opacity-50">
                {sendingWelcome ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send email
              </button>
            </div>
            <p className="text-alp-gray text-[10px] mt-3">The copied message includes their password — the email version doesn't (share it separately).</p>
          </div>
        </div>
      )}

      {list.length === 0 ? <p className="text-alp-gray text-sm text-center py-10">No crew yet — add your first contractor above.</p> : (
        <div className="space-y-3">
          {list.map((c) => (
            <div key={c.id} className="border border-alp-line rounded-lg bg-alp-card/30">
              <button onClick={() => openDetail(c.id)} data-testid={`crew-member-${c.id}`} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-alp-card/50 transition-colors">
                <span className="min-w-0 flex-1">
                  <span className="block text-alp-cream text-sm">{c.name} {!c.active && <span className="text-red-300 text-[10px] uppercase">· deactivated</span>}
                    {c.payoutRequest && <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-amber-300 border border-amber-400/40 rounded-full px-2 py-0.5" data-testid={`crew-payout-badge-${c.id}`}>Payout requested · {money(c.payoutRequest.amount)}</span>}
                  </span>
                  <span className="block text-alp-gray text-xs">{c.mobile ? `📱 ${c.mobile}` : `@${c.username}`}{c.affiliateCode ? ` · code ${c.affiliateCode} (${c.commissionPercent}%)` : ''} · {c.openTasks} open task{c.openTasks === 1 ? '' : 's'}</span>
                </span>
                <span className="text-right">
                  <span className="block text-alp-gold text-sm" data-testid={`crew-owed-${c.id}`}>{money(c.totalOwed)} owed</span>
                  <span className="block text-alp-gray text-[11px]">{money(c.workOwed)} work + {money(c.c_commissionOwed)} comm · {c.c_referredSales} referrals</span>
                </span>
                <ChevronDown size={15} className={`text-alp-gray transition-transform ${openId === c.id ? 'rotate-180' : ''}`} />
              </button>
              {openId === c.id && (
                <div className="border-t border-alp-line/60 p-4 space-y-5">
                  {c.bank ? (
                    <p className="text-xs text-alp-gray" data-testid={`crew-bank-${c.id}`}>
                      🏦 Pay to: <span className="text-alp-cream/90">{c.bank.accountName}</span> · BSB <span className="text-alp-gold">{c.bank.bsb}</span> · Acc <span className="text-alp-gold">{c.bank.accountNumber}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-amber-300/80">No bank details yet — they can add them in their portal under "Get paid".</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => pay(c)} disabled={c.totalOwed <= 0} data-testid={`crew-pay-${c.id}`}
                      className="inline-flex items-center gap-1.5 bg-alp-gold text-alp-black px-4 py-2 rounded text-xs tracking-[0.12em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-40"><Banknote size={14} /> Mark {money(c.totalOwed)} paid</button>
                    <button onClick={() => resendWelcome(c)} data-testid={`crew-welcome-resend-${c.id}`} className="inline-flex items-center gap-1.5 border border-alp-line text-alp-gray px-4 py-2 rounded text-xs tracking-[0.12em] uppercase hover:border-alp-gold hover:text-alp-gold"><Mail size={13} /> Welcome email</button>
                    <button onClick={() => toggleActive(c)} className="border border-alp-line text-alp-gray px-4 py-2 rounded text-xs tracking-[0.12em] uppercase hover:border-alp-gold hover:text-alp-gold">{c.active ? 'Deactivate' : 'Reactivate'}</button>
                  </div>

                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><ClipboardList size={12} /> Allocate a task</p>
                    <div className="flex flex-wrap gap-2">
                      <input value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} placeholder="Task (e.g. Label 50 BPC vials)" data-testid="crew-task-title" className="flex-1 min-w-[180px] bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream" />
                      <input value={task.dueDate} onChange={(e) => setTask({ ...task, dueDate: e.target.value })} placeholder="Due (opt)" className="w-28 bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream" />
                      <button onClick={() => addTask(c.id)} data-testid="crew-task-add" className="border border-alp-gold/50 text-alp-gold px-4 py-2 rounded text-xs tracking-[0.12em] uppercase hover:bg-alp-gold/10">Allocate</button>
                    </div>
                    {detail && detail.tasks.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {detail.tasks.map((t) => (
                          <div key={t.id} className="flex items-center gap-2 text-xs">
                            <span className={t.status === 'done' ? 'text-emerald-300' : 'text-amber-300'}>{t.status === 'done' ? '✓' : '○'}</span>
                            <span className={`flex-1 ${t.status === 'done' ? 'text-alp-gray line-through' : 'text-alp-cream/90'}`}>{t.title}{t.dueDate ? ` (due ${t.dueDate})` : ''}</span>
                            <button onClick={() => delTask(c.id, t.id)} className="text-alp-gray hover:text-red-300"><Trash2 size={12} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2">Work log</p>
                    {!detail ? <Loader2 className="animate-spin text-alp-gold" size={16} /> : detail.logs.length === 0 ? <p className="text-alp-gray text-xs">Nothing logged yet.</p> : (
                      <div className="border border-alp-line rounded divide-y divide-alp-line/50 max-h-56 overflow-y-auto">
                        {detail.logs.map((l) => (
                          <div key={l.id} className="flex items-center gap-2 px-3 py-2 text-xs">
                            <span className="text-alp-gray w-20 shrink-0">{l.date}</span>
                            <span className="flex-1 text-alp-cream/90 truncate">{l.label} × {l.qty}{l.note ? ` — ${l.note}` : ''}</span>
                            <span className="text-alp-gold">{money(l.amount)}</span>
                            <span className={`text-[9px] uppercase ${l.status === 'paid' ? 'text-emerald-300' : 'text-amber-300'}`}>{l.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {detail && detail.payouts.length > 0 && (
                      <p className="text-alp-gray text-[11px] mt-2">Last payout: {money(detail.payouts[0].total)} on {new Date(detail.payouts[0].createdAt).toLocaleDateString('en-AU')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
